import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { setPageMeta, breadcrumbLd } from '../utils/seo';
import { getHiringGuide, HIRING_GUIDES } from '../data/hiringGuides';
import { CaretDown, ArrowRight } from '@phosphor-icons/react';
import AdBanner from '../components/AdBanner';
import { getLinksForArticle } from '../data/internalLinks';

const Faq = ({ f, i }) => {
  const [open, setOpen] = useState(i === 0);
  return (
    <div className="border-2 border-neutral-900 dark:border-neutral-700 bg-white dark:bg-neutral-800">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-3 p-4 text-left" data-testid={`hg-faq-${i}`}>
        <span className="font-black text-neutral-900 dark:text-white">{f.q}</span>
        <CaretDown size={18} weight="bold" className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <p className="px-4 pb-4 text-neutral-600 dark:text-neutral-300 text-sm leading-relaxed">{f.a}</p>}
    </div>
  );
};

export default function HiringGuideDetail() {
  const { slug } = useParams();
  const guide = getHiringGuide(slug);

  useEffect(() => {
    if (!guide) return;
    window.scrollTo(0, 0);
    setPageMeta({
      title: guide.title,
      description: guide.description,
      ogType: 'article',
      url: `${window.location.origin}/hiring-guides/${guide.slug}`,
      jsonLd: [
        {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: guide.faqs.map((f) => ({
            '@type': 'Question', name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        },
        breadcrumbLd([
          { name: 'Home', url: window.location.origin },
          { name: 'Hiring Guides', url: `${window.location.origin}/hiring-guides` },
          { name: guide.h1 },
        ]),
      ],
    });
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!guide) {
    return (
      <div className="min-h-screen bg-[#E8E9EB] dark:bg-[#121212] flex items-center justify-center">
        <div className="text-center">
          <p className="font-black text-xl text-neutral-900 dark:text-white mb-4">Guide not found</p>
          <Link to="/hiring-guides" className="text-[#D95800] font-bold underline">Browse all hiring guides</Link>
        </div>
      </div>
    );
  }

  const others = HIRING_GUIDES.filter((g) => g.slug !== guide.slug).slice(0, 3);

  return (
    <div className="min-h-screen bg-[#E8E9EB] dark:bg-[#121212]" data-testid="hiring-guide-detail">
      <div className="bg-neutral-900 border-b-4 border-[#D95800]">
        <div className="max-w-3xl mx-auto px-4 py-12">
          <nav className="text-xs text-neutral-400 font-bold mb-4">
            <Link to="/" className="hover:text-white">Home</Link> / <Link to="/hiring-guides" className="hover:text-white" data-testid="hg-breadcrumb">Hiring Guides</Link>
          </nav>
          <h1 className="text-4xl sm:text-5xl font-black text-white uppercase leading-tight mb-4">{guide.h1}</h1>
          <p className="text-neutral-300 leading-relaxed">{guide.intro}</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-10 space-y-8">
        {guide.sections.map((s, i) => (
          <section key={i}>
            <h2 className="text-lg font-black uppercase text-neutral-900 dark:text-white mb-2">{s.heading}</h2>
            <p className="text-neutral-700 dark:text-neutral-300 leading-relaxed">{s.body}</p>
          </section>
        ))}

        <AdBanner placement="guides" />

        {/* Mid-content CTA */}
        <div className="bg-[#D95800] border-2 border-neutral-900 p-8 text-center shadow-[5px_5px_0px_#000]">
          <h2 className="text-2xl font-black uppercase text-white mb-2">Skip the guesswork</h2>
          <p className="text-orange-100 mb-5">Post your project once — verified, insured local pros send you free competing quotes, and escrow protects every payment.</p>
          <Link to="/get-quotes" className="inline-flex h-12 px-8 items-center bg-neutral-900 text-white font-black uppercase border-2 border-white" data-testid="hg-cta">
            Get 3 Free Quotes
          </Link>
        </div>

        <div>
          <h2 className="text-lg font-black uppercase text-neutral-900 dark:text-white mb-4">Frequently asked questions</h2>
          <div className="space-y-3">
            {guide.faqs.map((f, i) => <Faq key={i} f={f} i={i} />)}
          </div>
        </div>

        <div>
          <h2 className="text-sm font-black uppercase text-neutral-500 mb-3">Keep reading</h2>
          <div className="space-y-2">
            {others.map((g) => (
              <Link key={g.slug} to={`/hiring-guides/${g.slug}`}
                className="flex items-center justify-between bg-white dark:bg-neutral-800 border-2 border-neutral-900 dark:border-neutral-700 p-4 font-bold text-sm text-neutral-800 dark:text-neutral-200 hover:bg-orange-50"
                data-testid={`hg-related-${g.slug}`}>
                {g.h1} <ArrowRight size={16} weight="bold" className="text-[#D95800] shrink-0" />
              </Link>
            ))}
            <Link to="/guides" className="block text-center text-sm font-bold text-[#D95800] underline pt-2">See 2026 cost guides for every trade →</Link>
          </div>
        </div>

        {/* Internal links: related trades + cost guides */}
        {(() => {
          const links = getLinksForArticle(guide.slug);
          if (links.trades.length === 0 && links.guides.length === 0) return null;
          return (
            <div data-testid="hg-internal-links">
              <h2 className="text-sm font-black uppercase text-neutral-500 mb-3">Related resources</h2>
              <div className="flex flex-wrap gap-2">
                {links.trades.map((t) => (
                  <Link key={t.slug} to={`/hire/${t.slug}`}
                    className="px-3 py-2 bg-neutral-900 text-white border-2 border-neutral-900 text-sm font-bold hover:bg-neutral-700"
                    data-testid={`hg-trade-${t.slug}`}>
                    Hire {t.name}
                  </Link>
                ))}
                {links.guides.map((g) => (
                  <Link key={g.slug} to={`/guides/${g.slug}`}
                    className="px-3 py-2 bg-white dark:bg-neutral-800 border-2 border-neutral-900 dark:border-neutral-700 text-sm font-bold text-neutral-800 dark:text-neutral-200 hover:bg-orange-50"
                    data-testid={`hg-guide-${g.slug}`}>
                    {g.label}
                  </Link>
                ))}
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}
